import React, { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { dispatcher } from 'dispatcher';
import { debounce } from 'underscore';
import uuid from 'uuid-random';

import Overlay from '@core/js/components/common/Overlay';

import { SvgIcon } from '@interlayer/js/app/components/common/SvgIcon';
import { GameSearchResult } from '@interlayer/js/app/components/main/GameSearchGlobal/components/GameSearchResult';
import { InputShallow } from '@interlayer/js/components/common/Fields/Input/components/InputShallow';

import { IGameFeedCategoryConfig } from '@core/js/components/system/App/store/types';
import { useTranslations } from '@core/js/hooks/i18n';
import { scrollService } from '@core/js/services/scroll.service';
import { searchFlex } from '@core/js/utils/search.util';

import { GAME_EVENTS } from '@interlayer/js/app/components/main/Games/constants';
import { DEFAULT_SEARCH_LIST, GAME_SEARCH_HEADER_ID } from '@interlayer/js/app/components/main/GameSearchGlobal/constants';
import { useAvailableGames } from '@interlayer/js/app/components/main/GamesSearch/hooks/useAvailableGames';
import { useInDesktop } from '@interlayer/js/app/hooks/device/hooks/useMediaQuery';
import { useDynamicTags } from '@interlayer/js/app/hooks/useDynamicTags';
import { useHiddenDomElement } from '@interlayer/js/app/hooks/useHiddenDomElement';
import { useIsShowAnyPopup } from '@interlayer/js/app/hooks/useIsShowAnyPopup';
import { useSpeechRecognition } from '@interlayer/js/app/hooks/useSpeechRecognition';
import { clsx } from '@interlayer/js/app/utils/clsx.util';
import { useClickOutSide } from '@interlayer/js/app/utils/common.util';
import { DYNAMIC_TAG_NAMES } from '@interlayer/js/app/utils/constants';
import { EVENT_SHOW_LOGIN_POPUP } from '@interlayer/js/components/popups/LoginPopup/buzz/constants';
import { useUpdateEffect } from '@interlayer/js/hooks/useUpdateEffect';

import { TGamesSearchGlobalProps, TSearchList } from './types';

import style from './GameSearchGlobal.scss';

export const GamesSearchGlobal: React.FC<TGamesSearchGlobalProps> = ({ className, onCloseSearch }) => {
    const isDesktop = useInDesktop();
    const isShowAnyPopup = useIsShowAnyPopup();
    const inputRef = useRef<HTMLInputElement>(null);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const availableGames = useAvailableGames();
    const [isActive, setIsActive] = useState(false);
    const [valueInput, setValueInput] = useState('');
    const [selectedCategory, setSelectedCategory] = useState<null | IGameFeedCategoryConfig>(null);
    const [searchList, setSearchList] = useState<TSearchList[]>(DEFAULT_SEARCH_LIST);
    const [isPrepareSearchList, setIsPrepareSearchList] = useState(false);
    const t = useTranslations('playtech.mobile.system.games-search.');
    const [dTagVoiceGameSearch] = useDynamicTags([DYNAMIC_TAG_NAMES.voiceGameSearch]);

    const closeSearchBlock = useCallback(() => {
        setIsActive(false);
        setSearchList(DEFAULT_SEARCH_LIST);
        setSelectedCategory(null);
        setValueInput('');

        if (onCloseSearch) {
            onCloseSearch();
        }
    }, [onCloseSearch]);

    useClickOutSide(isDesktop && !isShowAnyPopup ? wrapperRef : null, closeSearchBlock);

    useEffect(() => {
        dispatcher.listen(EVENT_SHOW_LOGIN_POPUP, closeSearchBlock);

        return () => {
            dispatcher.removeListener(EVENT_SHOW_LOGIN_POPUP, closeSearchBlock);
        };
    }, [closeSearchBlock]);

    useUpdateEffect(() => {
        if (!isActive && inputRef.current) {
            inputRef.current.value = '';
            return;
        }

        if (isDesktop) {
            return;
        }

        const lockId = uuid();

        scrollService.disableBodyScroll(lockId);

        return () => scrollService.enableBodyScroll(lockId);
    }, [isActive, isDesktop]);

    const searchFunction = useMemo(
        () => searchFlex(availableGames),
        [availableGames]
    );

    const debounceSearch = useMemo(() => debounce(() => {
        if (!inputRef.current) {
            return;
        }

        const { value } = inputRef.current;

        if (value) {
            dispatcher.dispatch(GAME_EVENTS.GAME_PORTLET_SEARCH, { searchString: value });
        }

        setSearchList(searchFunction(value));
        setIsPrepareSearchList(false);
    }, 300), [searchFunction]);

    const handleChange = () => {
        if (!inputRef.current) {
            return;
        }

        const { value } = inputRef.current;

        setValueInput(value);

        if (value.length < 3) {
            setSearchList(DEFAULT_SEARCH_LIST);
            setIsPrepareSearchList(false);
            return;
        }

        setIsPrepareSearchList(true);
        setSelectedCategory(null);
        debounceSearch();
    };

    const handlerFocus = () => {
        setIsActive(true);

        dispatcher.dispatch('gameSearchInFocus');
    };

    const { isInDom, isShowed } = useHiddenDomElement({ isReady: isActive });

    const { isAvailable, result, handleStartStop } = useSpeechRecognition();

    useEffect(() => {
        if (inputRef.current && result) {
            inputRef.current.value = result;
            handleChange();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [result]);

    return (
        <>
            {isDesktop && isActive && <Overlay className={style.overlay} />}

            <div className={clsx(style.wrapper, className)} ref={wrapperRef}>
                <div className={style.inputWrapper}>
                    <InputShallow
                        ref={inputRef}
                        id={GAME_SEARCH_HEADER_ID}
                        type="text"
                        autoComplete="off"
                        className={style.input}
                        placeholder={t('placeholder.search')}
                        onChange={handleChange}
                        onFocus={handlerFocus}
                    />

                    {Boolean(isAvailable && dTagVoiceGameSearch) && (
                        <SvgIcon
                            type="microphone"
                            onClick={() => {
                                inputRef.current?.focus();
                                handleStartStop && handleStartStop();
                            }}
                            className={clsx(style.icon, style.recognitionIcon)}
                        />
                    )}

                    <SvgIcon
                        onClick={() => inputRef.current?.focus()}
                        className={style.icon}
                        type="search-thin"
                    />
                </div>

                {isInDom && (
                    <Suspense fallback="">
                        <GameSearchResult
                            isActive={isShowed && isActive}
                            inputRef={inputRef}
                            valueInput={valueInput}
                            searchList={searchList}
                            closeSearchBlock={closeSearchBlock}
                            isPrepareSearchList={isPrepareSearchList}
                            selectedCategory={selectedCategory}
                            setSelectedCategory={setSelectedCategory}
                        />
                    </Suspense>
                )}
            </div>
        </>
    );
};
